import {
  FormEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useParams, useSearchParams, useNavigate } from 'react-router-dom'

import { useAuth } from '../../_hooks/use-auth'
import { submitDiscoverInquiry } from '../../_data/discover'
import { getQuickPassProfileByUserId } from '../../_data/quickpass-profile'
import {
  resolveAnonSession,
  submitAnonymousInquiry,
} from '../../_data/anon-session'
import {
  hasCrossSiteStorageAccess,
  readCrossSiteIdentityToken,
} from '../../_utilities/cross-site-identity'
import { InquiryFieldDefinition } from '../../_types/inquiry-field'
import PhoneField from '../../components/phone-field/PhoneField'
import {
  InquireContextMessage,
  InquireIdentity,
  InquireListingContext,
  InquirePageState,
} from './types'
import {
  collectCustomFields,
  isEmbedded,
  parseInquiryFormConfig,
  requestOverlayClose,
  runAnonClaimPopup,
} from './helpers'
import {
  ContentCard,
  ErrorBanner,
  FieldGroup,
  FieldRow,
  Form,
  Heading,
  Label,
  ListingCard,
  ListingCardBody,
  ListingCardImage,
  ListingCardMeta,
  ListingCardMetaRow,
  ListingCardPrice,
  ListingCardSubtitle,
  ListingCardTitle,
  MessageTextarea,
  PageContainer,
  PrimaryButton,
  PrivacyNote,
  SecondaryButton,
  SelectInput,
  SendingAs,
  Spinner,
  StatusIndicator,
  SuccessIcon,
  TextInput,
  Wordmark,
} from './styled'

const EMPTY_IDENTITY: InquireIdentity = {
  mode: 'new',
  quickPassUserId: null,
  token: null,
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  displayName: '',
}

const SPINNER_TRANSITION = {
  rotate: 360,
  transition: { repeat: Infinity, duration: 0.7, ease: 'linear' },
} as const

const seedFieldValues = (
  curatedFields: InquiryFieldDefinition[],
  customFields: Record<string, string>,
): Record<string, string> => {
  const values: Record<string, string> = {}

  for (const field of curatedFields) {
    values[field.key] = customFields[field.key] ?? ''
  }

  return values
}

/**
 * Standalone inquiry page driven by the cross-site SDK (`QuickPass.inquire`).
 * Resolves the renter's identity down a three-rung ladder — signed-in
 * QuickPass session, then a recognised anonymous renter (cross-site cookie),
 * then a brand-new renter who types their details once into a first-party
 * popup — and submits a routed inquiry that lands in the partner's CRM as a
 * lead.
 *
 * Beyond the basics, the host site can switch on curated extra fields via the
 * URL (`?fields=budget,parking&required=phone,budget`). Those answers are
 * prefilled from the renter's QuickPass `customFields` (so they carry across
 * inquiries) and are saved back on submit.
 *
 * @returns {JSX.Element}
 */
const InquirePage = (): JSX.Element => {
  const { inventoryId } = useParams<{ inventoryId: string }>()
  const [searchParameters] = useSearchParams()
  const navigate = useNavigate()
  const { quickPassUser, isLoading } = useAuth()
  const resolutionStartedReference = useRef<boolean>(false)

  const listingTitle = searchParameters.get('title') ?? ''
  const formConfig = useMemo(
    () => parseInquiryFormConfig(searchParameters),
    [searchParameters],
  )

  const [state, setState] = useState<InquirePageState>({
    status: 'resolving',
    identity: EMPTY_IDENTITY,
    listing: null,
    contextInventoryId: '',
    fieldValues: {},
    message: '',
    errorMessage: '',
    alreadySent: false,
  })

  useEffect(() => {
    if (isLoading === true || resolutionStartedReference.current === true) {
      return
    }
    resolutionStartedReference.current = true

    const resolveIdentity = async (): Promise<void> => {
      const hasCuratedFields = formConfig.curatedFields.length > 0

      if (quickPassUser !== null) {
        const profile = hasCuratedFields
          ? await getQuickPassProfileByUserId(quickPassUser.id)
          : null

        setState(previous => ({
          ...previous,
          status: 'form',
          identity: {
            ...EMPTY_IDENTITY,
            mode: 'account',
            quickPassUserId: quickPassUser.id,
            email: quickPassUser.email,
            displayName: quickPassUser.displayName ?? quickPassUser.email,
          },
          fieldValues: seedFieldValues(
            formConfig.curatedFields,
            profile?.customFields ?? {},
          ),
        }))
        return
      }

      const hasAccess = await hasCrossSiteStorageAccess()

      if (hasAccess === true) {
        const token = readCrossSiteIdentityToken()

        if (token !== null) {
          const resolved = await resolveAnonSession(token)

          if (resolved !== null) {
            setState(previous => ({
              ...previous,
              status: 'form',
              identity: {
                mode: 'known-anon',
                quickPassUserId: resolved.quickPassUserId,
                token,
                firstName: resolved.firstName,
                lastName: resolved.lastName,
                email: resolved.email,
                phone: resolved.phone ?? '',
                displayName:
                  `${resolved.firstName} ${resolved.lastName}`.trim() ||
                  resolved.email,
              },
              fieldValues: seedFieldValues(
                formConfig.curatedFields,
                resolved.customFields,
              ),
            }))
            return
          }
        }
      }

      setState(previous => ({
        ...previous,
        status: 'form',
        fieldValues: seedFieldValues(formConfig.curatedFields, {}),
      }))
    }

    void resolveIdentity()
  }, [isLoading, quickPassUser, formConfig])

  useEffect(() => {
    const handleHostMessage = (event: MessageEvent): void => {
      const data = event.data as Partial<InquireContextMessage>

      if (
        data === null ||
        data.source !== 'quickpass-host' ||
        data.type !== 'inquire-context'
      ) {
        return
      }

      const incomingListing = data.payload?.listing ?? null
      const incomingListingId =
        typeof data.payload?.quickCasaListingId === 'string'
          ? data.payload.quickCasaListingId
          : ''

      setState(previous => ({
        ...previous,
        listing: incomingListing ?? previous.listing,
        contextInventoryId:
          incomingListingId !== ''
            ? incomingListingId
            : previous.contextInventoryId,
      }))
    }

    window.addEventListener('message', handleHostMessage)

    if (isEmbedded() === true) {
      window.parent.postMessage(
        { source: 'quickpass', type: 'inquire-ready' },
        '*',
      )
    }

    return () => {
      window.removeEventListener('message', handleHostMessage)
    }
  }, [])

  const updateIdentityField = useCallback(
    (field: keyof InquireIdentity, value: string): void => {
      setState(previous => ({
        ...previous,
        identity: { ...previous.identity, [field]: value },
      }))
    },
    [],
  )

  const updateFieldValue = useCallback((key: string, value: string): void => {
    setState(previous => ({
      ...previous,
      fieldValues: { ...previous.fieldValues, [key]: value },
    }))
  }, [])

  const finishWithResult = useCallback((alreadySent: boolean): void => {
    setState(previous => ({ ...previous, status: 'success', alreadySent }))
  }, [])

  const failWith = useCallback((errorMessage: string): void => {
    setState(previous => ({ ...previous, status: 'error', errorMessage }))
  }, [])

  const findValidationError = useCallback((): string | null => {
    const { identity, fieldValues, message } = state
    const { requiredKeys, curatedFields } = formConfig

    if (identity.mode === 'new') {
      if (identity.firstName.trim() === '' || identity.email.trim() === '') {
        return 'Please add your name and email so the team can reply.'
      }
      if (
        requiredKeys.includes('phone') === true &&
        identity.phone.trim() === ''
      ) {
        return 'Please add your phone number.'
      }
    }

    if (requiredKeys.includes('message') === true && message.trim() === '') {
      return 'Please add a message.'
    }

    for (const field of curatedFields) {
      if (
        requiredKeys.includes(field.key) === true &&
        (fieldValues[field.key] ?? '').trim() === ''
      ) {
        return `${field.label} is required.`
      }
    }

    return null
  }, [state, formConfig])

  const handleSubmit = useCallback(
    async (event: FormEvent<HTMLFormElement>): Promise<void> => {
      event.preventDefault()

      const effectiveInventoryId =
        typeof inventoryId === 'string' && inventoryId !== ''
          ? inventoryId
          : state.contextInventoryId

      if (effectiveInventoryId === '') {
        failWith('This listing is missing — please reopen from the listing.')
        return
      }

      const validationError = findValidationError()
      if (validationError !== null) {
        failWith(validationError)
        return
      }

      const { identity, message } = state
      const trimmedMessage = message.trim()
      const customFields = collectCustomFields(
        formConfig.curatedFields,
        state.fieldValues,
      )

      setState(previous => ({ ...previous, status: 'submitting' }))

      try {
        if (identity.mode === 'account' && identity.quickPassUserId !== null) {
          const result = await submitDiscoverInquiry({
            quickPassUserId: identity.quickPassUserId,
            inventoryId: effectiveInventoryId,
            message: trimmedMessage,
            customFields,
          })

          if (result === null) {
            failWith('We could not send your inquiry. Please try again.')
            return
          }

          finishWithResult(result.status === 'already-sent')
          return
        }

        let token = identity.token

        if (identity.mode === 'new') {
          token = await runAnonClaimPopup({
            firstName: identity.firstName.trim(),
            lastName: identity.lastName.trim(),
            email: identity.email.trim(),
            phone: identity.phone.trim() === '' ? null : identity.phone.trim(),
          })
        }

        if (token === null || token === '') {
          failWith('We could not set up your QuickPass. Please try again.')
          return
        }

        const result = await submitAnonymousInquiry({
          token,
          inventoryId: effectiveInventoryId,
          message: trimmedMessage,
          customFields,
        })

        if (result === null) {
          failWith('We could not send your inquiry. Please try again.')
          return
        }

        finishWithResult(result.status === 'already-sent')
      } catch (caughtError: unknown) {
        const description =
          caughtError instanceof Error
            ? caughtError.message
            : 'We could not send your inquiry. Please try again.'
        failWith(description)
      }
    },
    [
      inventoryId,
      state,
      formConfig,
      failWith,
      finishWithResult,
      findValidationError,
    ],
  )

  const handleDone = useCallback((): void => {
    if (isEmbedded() === true) {
      requestOverlayClose()
      return
    }

    navigate('/', { replace: true })
  }, [navigate])

  const handleMessageChange = useCallback((value: string): void => {
    setState(previous => ({ ...previous, message: value }))
  }, [])

  const { status, identity, fieldValues, message, errorMessage, alreadySent } =
    state
  const isSubmitting = status === 'submitting'
  const isKnownRenter = identity.mode !== 'new'
  const isMessageRequired = formConfig.requiredKeys.includes('message')
  const card: InquireListingContext | null = state.listing
  const cardTitle =
    card !== null && card.title !== '' ? card.title : listingTitle
  const showListingCard = card !== null || listingTitle !== ''

  const renderCuratedField = (field: InquiryFieldDefinition): JSX.Element => {
    const value = fieldValues[field.key] ?? ''
    const inputId = `inquire-field-${field.key}`
    const isRequired = formConfig.requiredKeys.includes(field.key)
    const label =
      isRequired === true ? field.label : `${field.label} (optional)`

    return (
      <FieldGroup key={field.key}>
        <Label htmlFor={inputId}>{label}</Label>
        {field.inputType === 'select' ? (
          <SelectInput
            id={inputId}
            value={value}
            onChange={event => updateFieldValue(field.key, event.target.value)}
            disabled={isSubmitting}
          >
            <option value="">Select…</option>
            {(field.options ?? []).map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </SelectInput>
        ) : (
          <TextInput
            id={inputId}
            type={field.inputType}
            inputMode={field.inputType === 'number' ? 'numeric' : undefined}
            placeholder={field.placeholder}
            value={value}
            onChange={event => updateFieldValue(field.key, event.target.value)}
            disabled={isSubmitting}
          />
        )}
      </FieldGroup>
    )
  }

  return (
    <PageContainer>
      <ContentCard>
        <Wordmark>QuickPass</Wordmark>

        {status === 'resolving' ? (
          <>
            <Heading>One sec</Heading>
            <StatusIndicator>
              <Spinner animate={SPINNER_TRANSITION} />
              <span>Getting your inquiry ready&hellip;</span>
            </StatusIndicator>
          </>
        ) : null}

        {status === 'form' || status === 'submitting' ? (
          <>
            <Heading>Send an inquiry</Heading>
            {showListingCard === true ? (
              <ListingCard>
                {card !== null && card.imageUrl !== '' ? (
                  <ListingCardImage src={card.imageUrl} alt={cardTitle} />
                ) : null}
                <ListingCardBody>
                  <ListingCardTitle>
                    {cardTitle !== '' ? cardTitle : 'This listing'}
                  </ListingCardTitle>
                  {card !== null && card.subtitle !== '' ? (
                    <ListingCardSubtitle>{card.subtitle}</ListingCardSubtitle>
                  ) : null}
                  {card !== null && (card.price !== '' || card.meta !== '') ? (
                    <ListingCardMetaRow>
                      {card.price !== '' ? (
                        <ListingCardPrice>{card.price}</ListingCardPrice>
                      ) : null}
                      {card.meta !== '' ? (
                        <ListingCardMeta>{card.meta}</ListingCardMeta>
                      ) : null}
                    </ListingCardMetaRow>
                  ) : null}
                </ListingCardBody>
              </ListingCard>
            ) : null}

            {isKnownRenter === true ? (
              <SendingAs>
                Sending as <strong>{identity.displayName}</strong>
              </SendingAs>
            ) : null}

            <Form onSubmit={handleSubmit}>
              {identity.mode === 'new' ? (
                <>
                  <FieldRow>
                    <FieldGroup>
                      <Label htmlFor="inquire-first-name">First name</Label>
                      <TextInput
                        id="inquire-first-name"
                        autoComplete="given-name"
                        placeholder="Jordan"
                        value={identity.firstName}
                        onChange={event =>
                          updateIdentityField('firstName', event.target.value)
                        }
                        disabled={isSubmitting}
                        required
                      />
                    </FieldGroup>
                    <FieldGroup>
                      <Label htmlFor="inquire-last-name">Last name</Label>
                      <TextInput
                        id="inquire-last-name"
                        autoComplete="family-name"
                        placeholder="Lee"
                        value={identity.lastName}
                        onChange={event =>
                          updateIdentityField('lastName', event.target.value)
                        }
                        disabled={isSubmitting}
                      />
                    </FieldGroup>
                  </FieldRow>

                  <FieldGroup>
                    <Label htmlFor="inquire-email">Email</Label>
                    <TextInput
                      id="inquire-email"
                      type="email"
                      autoComplete="email"
                      inputMode="email"
                      placeholder="you@example.com"
                      value={identity.email}
                      onChange={event =>
                        updateIdentityField('email', event.target.value)
                      }
                      disabled={isSubmitting}
                      required
                    />
                  </FieldGroup>

                  <FieldGroup>
                    <Label htmlFor="inquire-phone">
                      {formConfig.requiredKeys.includes('phone') === true
                        ? 'Phone'
                        : 'Phone (optional)'}
                    </Label>
                    <PhoneField
                      inputId="inquire-phone"
                      value={identity.phone}
                      onChange={value => updateIdentityField('phone', value)}
                      disabled={isSubmitting}
                    />
                  </FieldGroup>
                </>
              ) : null}

              {formConfig.curatedFields.map(renderCuratedField)}

              <FieldGroup>
                <Label htmlFor="inquire-message">
                  {isMessageRequired === true
                    ? 'Your message'
                    : 'Your message (optional)'}
                </Label>
                <MessageTextarea
                  id="inquire-message"
                  placeholder="Tell the team why this place works for you, when you'd like to move, anything that helps."
                  value={message}
                  onChange={event => handleMessageChange(event.target.value)}
                  disabled={isSubmitting}
                />
              </FieldGroup>

              {errorMessage !== '' ? (
                <ErrorBanner role="alert">{errorMessage}</ErrorBanner>
              ) : null}

              <PrimaryButton type="submit" disabled={isSubmitting}>
                {isSubmitting === true ? 'Sending…' : 'Send inquiry'}
              </PrimaryButton>

              {identity.mode === 'new' ? (
                <PrivacyNote>
                  We&apos;ll remember your details with QuickPass so you only
                  enter them once — across every site QuickPass works on.
                </PrivacyNote>
              ) : null}
            </Form>
          </>
        ) : null}

        {status === 'success' ? (
          <>
            <SuccessIcon aria-hidden="true">✓</SuccessIcon>
            <Heading>
              {alreadySent === true ? 'Already sent' : 'Inquiry sent'}
            </Heading>
            <SendingAs>
              {alreadySent === true
                ? 'You already reached out about this listing — the team has it.'
                : 'The leasing team has your inquiry and will be in touch.'}
            </SendingAs>
            <PrimaryButton type="button" onClick={handleDone}>
              Done
            </PrimaryButton>
          </>
        ) : null}

        {status === 'error' ? (
          <>
            <Heading>Something went sideways</Heading>
            <ErrorBanner role="alert">{errorMessage}</ErrorBanner>
            <SecondaryButton type="button" onClick={handleDone}>
              Close
            </SecondaryButton>
          </>
        ) : null}
      </ContentCard>
    </PageContainer>
  )
}

export default InquirePage
